import { useSyncExternalStore } from 'react';

/**
 * Times are stored as "HH:MM" (24-hour). People can type either way ("4:30 pm", "430pm",
 * "16:30", "1630"), and choose how times are shown on this device: 12-hour (4:30 pm) or 24-hour (16:30).
 */

const KEY = 'hoopify.time24';
const listeners = new Set<() => void>();

function browserPrefers24() {
  try {
    const hc = new Intl.DateTimeFormat(undefined, { hour: 'numeric' }).resolvedOptions().hourCycle;
    return hc === 'h23' || hc === 'h24';
  } catch {
    return false;
  }
}

export function uses24Hour(): boolean {
  try {
    const v = localStorage.getItem(KEY);
    if (v === '1') return true;
    if (v === '0') return false;
  } catch {
    /* fall through */
  }
  return browserPrefers24();
}

export function setUses24Hour(on: boolean) {
  try {
    localStorage.setItem(KEY, on ? '1' : '0');
  } catch {
    /* this device won't remember */
  }
  listeners.forEach((l) => l());
}

export function useTime24() {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    uses24Hour,
    () => false,
  );
}

/** Reads what someone typed. Returns "HH:MM", or null if it isn't a time. */
export function parseTime(text: string): string | null {
  const s = text.trim().toLowerCase().replace(/\s+/g, '').replace(/\./g, ':');
  if (!s) return null;
  const m = s.match(/^(\d{1,2})(?::?(\d{2}))?(am|pm|a|p)?$/);
  if (!m) return null;
  let h = Number(m[1]);
  const min = m[2] ? Number(m[2]) : 0;
  const ap = m[3]?.[0];
  if (min > 59) return null;
  if (ap) {
    if (h < 1 || h > 12) return null;
    if (ap === 'p' && h !== 12) h += 12;
    if (ap === 'a' && h === 12) h = 0;
  } else if (h > 23) return null;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** "16:30" → "4:30 pm", or "16:30" on 24-hour. */
export function formatTimeAs(t: string, h24: boolean) {
  const [h, m] = t.split(':').map(Number) as [number, number];
  if (h24) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'pm' : 'am'}`;
}
